import { infoApi } from '@shared/info-api-config';
import _ from '@luna/lodash';
import { documentsAndRequests } from '@shared/documents-and-requests-config';
import { appointments } from '@retail/appointments-config';
import { contactDataManagement } from '@shared/contact-data-management-config';
import { kyc } from '@retail/kyc-config';
import { messenger } from '@shared/messenger-config';
import { moneyAutomationFeatureConfigMap } from '@retail/money-automation-config';
import { thirdPartyProviders } from '@shared/third-party-providers-config';
import { cards } from '@retail/cards-config';
import { plugins } from '@retail/plugins-config';
import {
    AccountsCurrentFeatureConfig,
    AccountsFeatureConfig,
    BuildingSocietyFeatureConfig,
    ProductsFeatureConfig,
    SavingsFeatureConfig,
    StatementFeatureConfig,
} from '@retail/products-config';
import { exportFeatures, transactions } from '@retail/transactions-config';
import { featureConfigMap as store } from '@shared/store-config';
import { supportChatFeatureConfigMap } from '@shared/support-chat-config';
import { securitySettings } from '@shared/security-settings-config';

import { arrayOverwrite, baseFeatureConfig, extendFeatureConfigFromUrl } from './base';
import type { PartialRetailFeatureConfig } from './featureConfig';

export const countryFeatureConfig: PartialRetailFeatureConfig = {
    appointments: appointments.CZ,
    contactDataManagement: contactDataManagement.CZ,
    documentsAndRequests: documentsAndRequests.CZ,
    kyc: kyc.CZ,
    messenger: messenger.CZ,
    thirdPartyProviders: thirdPartyProviders.CZ,
    cards: cards.CZ,
    plugins: plugins.CZ,
    statements: StatementFeatureConfig.CZ,
    transactions: transactions.CZ,
    export: exportFeatures.CZ,
    savings: SavingsFeatureConfig.CZ,
    store: store.CZ,
    supportChat: supportChatFeatureConfigMap.CZ,
    securitySettings: securitySettings.CZ,
    reverseProxyVersion: 'v1',
    base: {
        country: 'CZ',
        newAccountModel: true,
        ibanCountryCode: 'CZ',
        defaultLanguage: 'cs',
        allowedLanguages: ['cs', 'en'],
        availableLanguages: ['cs', 'en', 'en-CX'],
        defaultCurrency: 'CZK',
        defaultCurrencySign: 'Kč',
        mainProductAmount: 'disposable',
        defaultCurrencyPre: false,
        doPreCurrencyCheck: false,
        thousandSeparator: ' ',
        saveLanguageChangesToPresto: false,
        sendRequestId: false,
        usingIban: false,
        unauthorizedStatusCode: '403',
        useSalutation: true,
        sendLanguageHeader: true,
        useVersionHeaders: false,
        latestVersionHeader: null,
        defaultTimezone: 'Europe/Prague',
        languageOverrides: {
            en: 'en-CX',
        },
    },
    footer: {
        links: {
            contacts: {
                cs: 'https://www.csas.cz/george_kontakty',
                en: 'https://www.csas.cz/george_contacts',
            },
            security: {
                cs: 'https://www.csas.cz/info_bezpecnost',
                en: 'https://www.csas.cz/en/about-us/security-and-data-protection',
            },
            termsAndConditions: {
                cs: 'https://www.csas.cz/george_podminky',
                en: 'https://www.csas.cz/george_conditions',
            },
        },
    },
    accounts: AccountsFeatureConfig.CZ,
    currentAccount: AccountsCurrentFeatureConfig.CZ,
    accountReservations: {
        isTransactionTypeUsed: true,
    },
    buildingSociety: BuildingSocietyFeatureConfig.CZ,
    pfm: {
        additionalKeywordFields: [
            'merchantName',
            'partnerName',
            'note',
            'bookingTypeTranslation',
            'receiverReference',
            'senderReference',
            'paymentReference',
            'location',
        ],
    },
    settings: {
        changePassword: false,
    },
    handover: {
        handoverTokenExchange: true,
    },
    moneyAutomation: moneyAutomationFeatureConfigMap.CZ,
    infoApi: infoApi.CZ,
    devTools: {
        availableApiTargets: {
            'FAT BLUE': ['https://george-preview.csast.csas.cz'],
            'FAT GREEN': ['https://george.csast.csas.cz'],
        },
        availableEnvironments: {
            Local: 'http://localhost:8080',
            Dev: 'https://cz.dev.beeone.at',
            'FAT 1': 'https://george.csast.csas.cz',
            'FAT 2': 'https://george-fat2.csast.csas.cz',
            'UAT 1': 'https://george-uat.csast.csas.cz',
            'UAT 2': 'https://george-uat2.csast.csas.cz',
            'FAT BLUE': 'https://george-preview.csast.csas.cz',
            'FAT GREEN': 'https://george.csast.csas.cz',
            PERF: 'https://george-pred.csast.csas.cz',
        },
    },
    tracking: {
        appId: 'INT66',
        appName: 'GeorgeWeb_Retail_CZ',
        instituteId: 778,
        posthog: {
            shouldUseExternalIdAsDistinctId: true,
        },
    },
    products: ProductsFeatureConfig.CZ,
    allowedRedirectDomains: ['csas.cz'],
    advisorAutoLogout: {
        timeoutInSeconds: 2700,
        timerThreshold: 60,
        alertThreshold: 30,
    },
};

const featureConfigWithBase = _.merge({}, baseFeatureConfig, countryFeatureConfig, arrayOverwrite);
export const featureConfig = extendFeatureConfigFromUrl(featureConfigWithBase);
